import { type HTMLAttributes, type ReactNode } from 'react'

type Tone = 'paper' | 'cream-card' | 'success' | 'warn' | 'info' | 'dashed'

type Props = HTMLAttributes<HTMLDivElement> & {
  tone?: Tone
  padding?: 'sm' | 'md' | 'lg' | 'none'
  children: ReactNode
}

const TONE_CLASS: Record<Tone, string> = {
  paper: 'bg-paper border border-ink-100',
  'cream-card': 'bg-cream-card border border-ink-100',
  success: 'bg-green-50 border border-green-100 text-green-800',
  warn: 'bg-[#fdf3d2] border border-[#e7d088] text-[#5f4807]',
  // Hex fill instead of `bg-info/10` — Tailwind's opacity modifier
  // needs an `<alpha-value>`-aware color definition, and `info` maps
  // to a bare `var(--info)` in the preset. Left as-is the tint doesn't
  // render and the card looks like a ghost outline; hardcoding the
  // 10%/30% tints matches the same pattern `warn` uses.
  info: 'bg-[#e6effb] border border-[#a8c3ee] text-info',
  dashed: 'bg-cream-card border border-dashed border-ink-200',
}

const PADDING_CLASS = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
}

const Card = ({
  tone = 'paper',
  padding = 'md',
  children,
  className = '',
  ...rest
}: Props) => (
  <div
    className={`rounded-lg ${TONE_CLASS[tone]} ${PADDING_CLASS[padding]} ${className}`}
    {...rest}
  >
    {children}
  </div>
)

export default Card
